import { useMemo } from 'react';
import type {
  NavigatorProperties,
  CreateNavigationButtonParams,
  NavigationButtonProps,
  FlowNavigationProps,
  TransitioningDirection,
} from '../types/flow-navigation';
import { type FlowNavigationFn } from './flow-step-navigation.util';
import { replaceTemplate } from './template-replacer';

/**
 * Creates navigation button props with proper type safety and state management
 */
const createNavigationButtonProps = ({
  buttonConfig,
  shouldHide,
  flowNavigationFn,
  direction,
  transitioningDirection,
}: CreateNavigationButtonParams): NavigationButtonProps | undefined => {
  if (!buttonConfig || !buttonConfig.label || shouldHide) {
    return undefined;
  }

  // Show loading only on the specific button being clicked
  const loading = transitioningDirection === direction;

  // Disable both buttons when any blocking transition is happening
  const isAnyBlockingTransition = transitioningDirection !== 0;
  const isButtonConfigDisabled =
    buttonConfig.disabled || isAnyBlockingTransition;

  return {
    ...buttonConfig,
    loading,
    disabled: !!isButtonConfigDisabled,
    onClick: async () => {
      // Prevent action if button is disabled or loading
      if (isButtonConfigDisabled || loading) return;

      // Only proceed with navigation if the step action succeeded (returned null/undefined)
      flowNavigationFn(direction);
    },
  };
};

/**
 * Custom hook for navigation labels with template replacement
 * Optimized with useMemo for better performance
 */
export const getNavigationLabels = (
  navigator: NavigatorProperties | undefined,
  currentStepIndex: number,
  numSteps: number,
  completionPercentage: number,
) => {
  const templateValues = {
    currentStep: currentStepIndex + 1,
    lastStep: numSteps,
    currentStepIndex,
    percentage: completionPercentage,
  };
  const accessibilityLabel =
    navigator?.progressBar?.accessibilityLabel &&
    replaceTemplate(navigator.progressBar.accessibilityLabel, templateValues);

  // If progressBar is defined, use the progressBar label, otherwise use the navigator label
  // This is to support the case where the progressBar is not defined, but the navigator label is
  let label;
  if (navigator?.progressBar) {
    label =
      navigator?.progressBar?.label &&
      replaceTemplate(navigator.progressBar?.label, templateValues);
  } else {
    label =
      navigator?.label && replaceTemplate(navigator.label, templateValues);
  }

  const leadingValue =
    navigator?.progressBar?.leadingValue &&
    replaceTemplate(navigator.progressBar.leadingValue, templateValues);

  const trailingValue =
    navigator?.progressBar?.trailingValue &&
    replaceTemplate(navigator.progressBar.trailingValue, templateValues);

  return {
    label,
    accessibilityLabel,
    leadingValue,
    trailingValue,
  };
};

type UseNavigationPropsParams = {
  navigator: NavigatorProperties | undefined;
  completionPercentage: number;
  flowNavigationFn: FlowNavigationFn;
  currentStepIndex: number;
  numSteps: number;
  transitioningDirection: TransitioningDirection;
  navRef?: React.RefObject<HTMLDivElement>;
};

/**
 * Custom hook for navigation properties with memoized performance optimization
 */
export const useNavigationProps = ({
  navRef,
  navigator,
  completionPercentage,
  flowNavigationFn,
  currentStepIndex,
  numSteps,
  transitioningDirection,
}: UseNavigationPropsParams): FlowNavigationProps =>
  useMemo(() => {
    // Get navigation labels with template replacement
    let progressBar: any;

    if (navigator?.progressBar) {
      progressBar = {
        ...navigator?.progressBar,
        fillProgressValue: completionPercentage,
        ...getNavigationLabels(
          navigator,
          currentStepIndex,
          numSteps,
          completionPercentage,
        ),
      };
    }

    const navigatorLabel = getNavigationLabels(
      navigator,
      currentStepIndex,
      numSteps,
      completionPercentage,
    );

    const leadingButton = createNavigationButtonProps({
      buttonConfig: navigator?.leadingButton,
      shouldHide: currentStepIndex === 0,
      flowNavigationFn,
      direction: -1,
      transitioningDirection,
    });

    // Check if user is on the last step
    const isLastStep = currentStepIndex === numSteps - 1;

    // Use lastStepTrailingButton if present and user is on the last step
    const trailingButtonConfig =
      isLastStep && navigator?.lastStepTrailingButton
        ? navigator.lastStepTrailingButton
        : navigator?.trailingButton;

    const trailingButton = createNavigationButtonProps({
      buttonConfig: trailingButtonConfig,
      shouldHide: false,
      flowNavigationFn,
      direction: 1,
      transitioningDirection,
    });

    // Extract Masonry-specific props to prevent them from being passed to FlowNavigation
    const {
      lastStepTrailingButton,
      position,
      ...navigatorWithoutMasonryProps
    } = navigator || {};

    return {
      ...navigatorWithoutMasonryProps,
      label: navigatorLabel.label,
      progressBar,
      leadingButton,
      trailingButton,
      ref: navRef,
    };
  }, [
    completionPercentage,
    currentStepIndex,
    flowNavigationFn,
    transitioningDirection,
    navigator,
    numSteps,
    navRef,
  ]);
